import { CombineOperator, CreateLocationRequest, GetPointResponse, LatLng, Location, SubTagSearchType, TagSearchType, TopLevelTagSearchType } from '../types';
import { isDefined, undefinedIfEmpty } from './utils';

import topicData from './state/topic-data';

/** Strips diacritics/accents so "café" matches "cafe" and vice versa. */
export function normalizeSearch(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export const categoryFilterFn = (p: GetPointResponse, selectedCategories: string[]) => {
  // if no categories are selected, return true
  if (selectedCategories.length === 0) {
    return true;
  }
  // Does the point's category match any of the selected categories.
  const pointCategory =
      topicData.categories.find((c) => c._id?.$oid === p.topic_category_id.$oid)
          ?.name ?? '';
  const matches = selectedCategories.includes(pointCategory) ?? false;

  return matches;
};


function searchValue(obj: any, keyword: any) {
  return normalizeSearch(JSON.stringify(obj)).includes(normalizeSearch(keyword));
}

export const keywordSearchFn = (p: GetPointResponse, keywordsList: string[]) => {
  return keywordsList.some((keyword) => searchValue(p, keyword));
};

export const likesFilterFn = (p: GetPointResponse, cookie: string) => {
  if (!isDefined(undefinedIfEmpty(cookie))) {
    return true;
  }
  return p?.likes?.includes(cookie) ?? false;
};

export function tagsFilterFn(point: GetPointResponse, search: TagSearchType): boolean {
  if (search.tags.length === 0) {
    // No tags to evaluate, return true - no filtering based on tags
    return true;
  }

  // iterate through each element in the tags array and run checkTags. if the top level operator is 'and', then all responses must be true, if the operator is 'or', then at least one response must be true
  const matches = search.tags.map((t) => {
    const matches = checkTags(point, t);
    return matches;
  });

  if (search.operator === CombineOperator.And) {
    return matches.every((m) => m);
  }

  if (search.operator === CombineOperator.Or) {
    return matches.some((m) => m);
  }

  return true;
}

// Check if point is within X distance of a location
export function checkPointWithinDistance(point: GetPointResponse, user_location: LatLng | undefined, distance_meters: number): boolean {
  if (!user_location) {
    // No user location provided, return false
    return false;
  }
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(point.location.latLng.lat, point.location.latLng.lng),
        new google.maps.LatLng(user_location.lat, user_location.lng)
    );
  return distance <= distance_meters;
}


export function checkTags(point: GetPointResponse, search: TopLevelTagSearchType | SubTagSearchType): boolean {
  if (typeof search === 'object' && 'subTags' in search) {
    if (search.operator === CombineOperator.And) {
      if (!checkForAllListedTagsOnPoint(point, search.subTags)) {
        return false;
      }
    } else if (search.operator === CombineOperator.Or) {
      if (!checkAnyListedTagsOnPoint(point, search.subTags)) {
        return false;
      }
    }
  } else {
    // this is a top level tag and just needs to be checked if it exists on the point
    if (!checkTagOnPoint(point, search.topLevelTag)) {
      return false;
    }
  }
  return true;
}

// Check if all tags in an array exist on a point. Works for tags or sub-tags because we return these in the same format
function checkForAllListedTagsOnPoint(point: GetPointResponse, tags: string[]): boolean {
  // see if all listed subtags are in the point
  return tags.every((tag) =>
    checkTagOnPoint(point, tag)
  );
}

// Check if any tags in an array exist on a point. Works for tags or sub-tags because we return these in the same format
function checkAnyListedTagsOnPoint(point: GetPointResponse, tags: string[]): boolean {
  // Implement your logic to check if at least one sub-tag matches the criteria
  return tags.some((tag) =>
    checkTagOnPoint(point, tag)
  );
}

// Check if a single tag exists on a point
function checkTagOnPoint(point: GetPointResponse, subTag: string): boolean {
  return point.tag_info?.user_tags.some((userTag) => userTag.name === subTag) ?? false;
}


// Helper Function
export function pointSearch(p: GetPointResponse, search: string): boolean {
    search = normalizeSearch(search);
    const searchablePointFields: Array<string | undefined> = [];
    const tagNames = new Set<string>();

    searchablePointFields.push(
        normalizeSearch(p.name ?? ''),
        normalizeSearch(p.location.search ?? ''),
        normalizeSearch(p.description ?? ''),
        normalizeSearch(p.email ?? ''),
        normalizeSearch(p.number ?? ''),
        normalizeSearch(p.custom_information?.custom_field_entries?.map((entry) => entry.field_value ?? '').join(' ') ?? ''),
        normalizeSearch(p.url ?? ''),
    );

    p.tag_info?.user_tags.forEach(tag => tagNames.add(normalizeSearch(tag.name)));

    const categoryName = topicData.categories
        .find((c) => c._id?.$oid === p.topic_category_id.$oid)?.name;

    const matches = searchablePointFields.some((f) => f?.includes(search)) ||
        tagNames.has(search) ||
        (categoryName ? normalizeSearch(categoryName).includes(search) : false) ||
        (!p.active && search === 'inactive');

    return matches;
}

// Define the function to check if two locations are the same
export function areLocationsSame(location1: Location | CreateLocationRequest | undefined, location2: Location | CreateLocationRequest |undefined): boolean {
    if (!location1 || !location2) {
        return false;
    }
    return location1.place_id === location2.place_id
        && location1.latLng?.lat === location2.latLng?.lat
        && location1.latLng?.lng === location2.latLng?.lng;
}